import { Controller } from "@hotwired/stimulus"

// Identifier: graficos-painel
// Desenha os gráficos Chart.js 2.9.3 da faixa "Visão analítica" do painel AI.
// Os dados vêm de um <script type="application/json" id="dados-graficos"> dentro
// do elemento do controller. Os rótulos centrais do donut (#empenho) e do
// medidor (#contrato) são overlays HTML na partial — aqui só desenhamos os anéis.
//
// Targets (todos opcionais — guardados com try/catch):
//   empenho     doughnut  — consumido × saldo
//   combustivel pie       — litros por combustível
//   secretaria  pie       — litros por secretaria
//   marca       pie       — litros por marca
//   veiculo     horizontalBar — litros por veículo
//   motorista   horizontalBar — litros por motorista
//   media       horizontalBar — km/L por secretaria
//   contrato    doughnut  — dias decorridos × restantes (janela 60 dias)

const PALETTE = ["#1ba9c7", "#2f9e44", "#e8a33d", "#6c5ce7", "#e44c48", "#3aa6c4"]
const BRAND   = "#1ba9c7"

export default class extends Controller {
  static targets = [
    "empenho", "combustivel", "secretaria", "marca",
    "veiculo", "motorista", "media", "contrato"
  ]

  connect() {
    this._charts = []
    this._waitTimer = null
    this._waitForChart(() => this._render())
  }

  disconnect() {
    if (this._waitTimer) { clearTimeout(this._waitTimer); this._waitTimer = null }
    this._charts.forEach(c => c.destroy())
    this._charts = []
  }

  // ─── private ────────────────────────────────────────────────────────────────

  _waitForChart(callback, attempt = 0) {
    if (typeof Chart !== "undefined") { callback(); return }
    if (attempt >= 40) return // ~4 s — desiste em silêncio
    this._waitTimer = setTimeout(() => this._waitForChart(callback, attempt + 1), 100)
  }

  _render() {
    const d = this._parseDados()
    if (!d) return

    this._pie("combustivelTarget", d.combustivel)
    this._pie("secretariaTarget",  d.secretaria)
    this._pie("marcaTarget",       d.marca)
    this._bars("veiculoTarget",    d.veiculo,   null)
    this._bars("motoristaTarget",  d.motorista, null)
    this._bars("mediaTarget",      d.media_kml, " km/L")
    this._donutEmpenho(d.empenho)
    this._medidorContrato(d.contrato)
  }

  _parseDados() {
    const el = this.element.querySelector("#dados-graficos") ||
               document.getElementById("dados-graficos")
    if (!el) return null
    try { return JSON.parse(el.textContent) } catch (e) { return null }
  }

  // ─── pizzas (#combustivel, #secretaria, #marca) ─────────────────────────────

  _pie(targetProp, fatias) {
    const canvas = this._canvas(targetProp)
    if (!canvas) return
    if (!fatias || fatias.length === 0) { this._hide(canvas); return }

    const labels = fatias.map(f => f.rotulo)
    const data   = fatias.map(f => f.valor)
    const colors = fatias.map((_, i) => PALETTE[i % PALETTE.length])

    this._charts.push(new Chart(canvas, {
      type: "pie",
      data: { labels, datasets: [{ data, backgroundColor: colors, borderWidth: 1 }] },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { position: "bottom", labels: { boxWidth: 12, fontSize: 11 } }
      }
    }))
  }

  // ─── barras horizontais (#veiculo, #motorista, #media) ──────────────────────

  _bars(targetProp, fatias, sufixo) {
    const canvas = this._canvas(targetProp)
    if (!canvas) return
    if (!fatias || fatias.length === 0) { this._hide(canvas); return }

    const labels = fatias.map(f => f.rotulo)
    const data   = fatias.map(f => f.valor)
    const xTicks = sufixo
      ? { callback: value => value + sufixo, beginAtZero: true }
      : { beginAtZero: true }

    this._charts.push(new Chart(canvas, {
      type: "horizontalBar",
      data: { labels, datasets: [{ data, backgroundColor: BRAND, borderWidth: 0 }] },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          xAxes: [{ ticks: xTicks }],
          yAxes: [{ ticks: { beginAtZero: true } }]
        }
      }
    }))
  }

  // ─── donut empenho (#empenho) — rótulo central é overlay HTML ───────────────

  _donutEmpenho(empenho) {
    const canvas = this._canvas("empenhoTarget")
    if (!canvas) return
    if (!empenho) { this._hide(canvas); return }

    this._charts.push(new Chart(canvas, {
      type: "doughnut",
      data: {
        labels: ["Consumido", "Saldo"],
        datasets: [{
          data: [empenho.consumido || 0, empenho.saldo || 0],
          backgroundColor: ["#1ba9c7", "#eef1f4"],
          borderWidth: 0
        }]
      },
      options: { responsive: true, maintainAspectRatio: false, legend: { display: false }, cutoutPercentage: 72 }
    }))
  }

  // ─── medidor contrato (#contrato) — janela 60 dias, número é overlay HTML ───

  _medidorContrato(contrato) {
    const canvas = this._canvas("contratoTarget")
    if (!canvas) return
    if (!contrato) { this._hide(canvas); return }

    const dias      = contrato.dias_restantes
    const decorrido = Math.max(0, 60 - dias)
    const restante  = Math.min(Math.max(dias, 0), 60)

    this._charts.push(new Chart(canvas, {
      type: "doughnut",
      data: {
        labels: ["Decorrido", "Restante"],
        datasets: [{
          data: [decorrido, restante],
          backgroundColor: ["#eef1f4", "#e8a33d"],
          borderWidth: 0
        }]
      },
      options: { responsive: true, maintainAspectRatio: false, legend: { display: false }, cutoutPercentage: 78 }
    }))
  }

  // ─── helpers ────────────────────────────────────────────────────────────────

  // Resolve `this[prop]` com segurança (target pode não existir).
  _canvas(targetProp) {
    try { return this[targetProp] } catch (_) { return null }
  }

  // Esconde o `.column` (Bulma) que envolve o canvas quando não há dados.
  _hide(canvas) {
    const col = canvas.closest(".column")
    if (col) col.classList.add("is-hidden")
  }
}
